import { Plus } from "lucide-react";
import { FormControl, FormItem } from "@/components/ui/form";
import { Badge } from "@/components/ui/badge";
import { EnhancedMultiSelect } from "@/components/ui/enhanced-multiselect";
import type { LabelDefinition } from "@/types";
import type { EnhancedSelectOption } from "@/components/ui/enhanced-multiselect";
import React from "react";
import { useLingui } from "@lingui/react";

interface ChildLabelSelectionProps {
  selectedLabels: string[];
  onLabelsChange: (labels: string[]) => void;
  labels: LabelDefinition[];
  /** Optional: the field config that feeds this component may not carry one. */
  onManageLabels?: () => void;
}

// Special value for the "Create new label" option
const ADD_LABEL_VALUE = "__add_new_label__";

export function ChildLabelSelection({
  selectedLabels,
  onLabelsChange,
  labels,
  onManageLabels,
}: ChildLabelSelectionProps) {
  const { i18n } = useLingui();
  // Handle selection change, intercepting the "Create new label" option
  const handleChange = React.useCallback(
    (newValues: string[]) => {
      // Check if the "Create new label" option was selected
      if (newValues.includes(ADD_LABEL_VALUE)) {
        // Remove the special value from the selection
        const filteredValues = newValues.filter((v) => v !== ADD_LABEL_VALUE);
        onLabelsChange(filteredValues);
        // Open the label manager modal
        onManageLabels?.();
      } else {
        // Normal selection change
        onLabelsChange(newValues);
      }
    },
    [onLabelsChange, onManageLabels]
  );

  // Create options list with "Create new label" at the bottom
  const options = React.useMemo(() => {
    const labelOptions: EnhancedSelectOption[] = labels
      // Type predicate, not a bare boolean: the `!= null` test has to narrow `id` for
      // `value` to satisfy `EnhancedSelectOption['value']: string`.
      .filter((label): label is typeof label & { id: string } => label.id != null)
      .map((label) => ({
        value: label.id,
        label: label.label || "",
        color: label.color,
      }));

    // Add the "Create new label" option at the end
    const addLabelOption: EnhancedSelectOption = {
      value: ADD_LABEL_VALUE,
      label: i18n._({ id: "children.labels.createNew", message: "Create new label" }),
      emoji: "➕",
      hideCheckbox: true,
    };

    return [...labelOptions, addLabelOption];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n is a stable instance; the locale drives re-translation.
  }, [labels, i18n.locale]);

  // Custom render for the "Create new label" option to make it visually distinct
  const renderOption = React.useCallback(
    (option: EnhancedSelectOption) => {
      if (option.value === ADD_LABEL_VALUE) {
        return (
          <div className="flex items-center gap-2 font-medium text-primary">
            <Plus className="h-4 w-4" />
            <span>{option.label}</span>
          </div>
        );
      }
      // For regular label options with color
      if (option.color) {
        return (
          <Badge
            variant="secondary"
            style={{
              backgroundColor: option.color ? `${option.color}20` : undefined,
              borderColor: option.color || undefined,
              color: option.color || undefined,
            }}
            className="cursor-pointer truncate"
          >
            {option.label}
          </Badge>
        );
      }
      // Default rendering
      return <span>{option.label}</span>;
    },
    []
  );

  return (
    <FormItem>
      <FormControl>
        <div className="space-y-3">
          <EnhancedMultiSelect
            value={selectedLabels}
            onChange={handleChange}
            options={options}
            placeholder={i18n._({ id: "common.selectLabels", message: "Select labels..." })}
            className="w-full"
            renderOption={renderOption}
          />

          {/* Display selected labels */}
          <div className="flex flex-wrap gap-2">
            {selectedLabels.map((labelId) => {
              const label = labels.find(
                (l: LabelDefinition) => l.id === labelId
              );
              if (!label) return null;
              return (
                <Badge
                  key={label.id}
                  variant="secondary"
                  style={{
                    backgroundColor: label.color
                      ? `${label.color}20`
                      : undefined,
                    borderColor: label.color || undefined,
                    color: label.color || undefined,
                  }}
                >
                  {label.label}
                </Badge>
              );
            })}
          </div>
        </div>
      </FormControl>
    </FormItem>
  );
}